import { Dispatch, SetStateAction, useEffect } from 'react';
import { Button, DatePicker, Form } from 'antd';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { endOfDay } from 'date-fns';
import PeriodRangeDropdown from '../../shared/components/dropdowns/period-range-dropdown/PeriodRangeDropdown';
import { downloadCSV } from '../../shared/helpers/csv-download.helper';
import { useLazyQuery } from '@apollo/client';
import { GET_AFFILIATE_COMMISSION_EXPORT } from '../commission-report/CommissionReport.gql';
import useSpinner from '../../hooks/layout/useSpinner';

const { RangePicker } = DatePicker;

interface Props {
    setFilters: Dispatch<SetStateAction<{ startDate: Date, endDate: Date }>>;
    setPageNo: React.Dispatch<React.SetStateAction<number>>;
    filters: { startDate: Date, endDate: Date };
}

const RegFtdFilter = ({ setFilters, filters, setPageNo }: Props) => {
    const [queryForm] = Form.useForm();
    const { setLoading } = useSpinner();
    const onFinish = (fieldsValue: any) => {
        emitFilter(fieldsValue.datePicker);
    }
    const [getExportCSVPayload, { data: getData, loading: getLoading }] = useLazyQuery(GET_AFFILIATE_COMMISSION_EXPORT, { fetchPolicy: 'network-only' });

    const exportFile = () => {
        try {
            getExportCSVPayload({ variables: { affiliateCommissionReportQueryInput: filters } });
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        if (getData) {
            const affiliateCommExport = getData && getData.affiliateCommExport ? getData.affiliateCommExport : null;
            if (affiliateCommExport) {
                downloadCSV(affiliateCommExport.csv, "Registrations & FTDs");
            }
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getData]);

    useEffect(() => {
        setLoading(getLoading); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getLoading]);

    const reset = () => {
        queryForm.resetFields();
        emitFilter([moment().subtract(1, "months"), moment()]);
    }

    const emitFilter = (datePicker: any) => {
        let periodRange = queryForm.getFieldValue('periodRange');
        let startDate = new Date();
        let endDate = new Date();

        if (periodRange) {
            switch (periodRange) {
                case 'TODAY':
                    startDate = moment().startOf('day').toDate();
                    endDate = moment().endOf('day').toDate();
                    break;
                case 'YESTERDAY':
                    startDate = moment().subtract(1, 'days').startOf('day').toDate();
                    endDate = moment().subtract(1, 'days').endOf('day').toDate();
                    break;
                case 'THIS-WEEK':
                    startDate = moment().startOf('week').toDate();
                    endDate = moment().endOf('week').toDate();
                    break;
                case 'LAST-WEEK':
                    startDate = moment().subtract(1, 'week').startOf('week').toDate();
                    endDate = moment().subtract(1, 'week').endOf('week').toDate();
                    break;
                case 'THIS-MONTH':
                    startDate = moment().startOf('month').toDate();
                    endDate = moment().endOf('month').toDate();
                    break;
                case 'LAST-MONTH':
                    startDate = moment().subtract(1, 'month').startOf('month').toDate();
                    endDate = moment().subtract(1, 'month').endOf('month').toDate();
                    break;
                case 'THIS-YEAR':
                    startDate = moment().startOf('year').toDate();
                    endDate = moment().endOf('year').toDate();
                    break;
                case 'LAST-YEAR':
                    startDate = moment().subtract(1, 'year').startOf('year').toDate();
                    endDate = moment().subtract(1, 'year').endOf('year').toDate();
                    break;
                default:
                    return null;
            }
        } else {
            startDate = datePicker && datePicker.length > 0 ?
                new Date(datePicker[0]) :
                new Date(moment().startOf('month').toString());
            endDate = datePicker && datePicker.length > 0 ?
                new Date(datePicker[1]) :
                endOfDay(new Date());
        }

        setFilters({
            startDate, endDate,
        });
        setPageNo(1);
    }

    return (
        <>
            <Form layout="inline"
                form={queryForm}
                onFinish={onFinish}
                className="filter-panel"
                initialValues={{
                    datePicker: [moment().subtract(1, "months"), moment()],
                }}
            >
                <Form.Item name="datePicker" label="Created Date" wrapperCol={{ span: 14 }}>
                    <RangePicker />
                </Form.Item>
                <PeriodRangeDropdown />
                <Form.Item className="filter-button-group">
                    <Button type="primary" htmlType="submit"><FormattedMessage id='FILTER_BTN_SEARCH' /></Button>
                    <Button type="primary" onClick={reset} danger><FormattedMessage id='FILTER_BTN_RESET' /></Button>
                    <Button type="primary" onClick={exportFile}><FormattedMessage id='FILTER_BTN_EXPORT' /></Button>
                </Form.Item>
            </Form>
        </>
    )
}

export default RegFtdFilter;